/**
 * The structured "When → Then" rule fields: a labeled WHEN block (one trigger)
 * and a THEN block (an ordered list of actions), each assembled from dropdowns,
 * with a live plain-English summary underneath. Deliberately NOT a monday-style
 * inline sentence — the editing surface is structured controls.
 *
 * Fully controlled + dialog-free: the parent (`AutomationsPanel`) owns the draft
 * state and renders this inside its single dialog as a swapped-in "screen", so
 * there's no modal-on-modal.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
} from '@monksflow/monks-ui';
import { ArrowDown, Plus, Trash2 } from 'lucide-react';

import type { TrackerBoardType } from '@/entities/tracker';
import type {
  AutomationAction,
  AutomationConditions,
  AutomationTrigger,
  AutomationValueOperator,
} from '@/entities/tracker-automation';

import type { CustomColumn, StatusOption } from '../../lib/useTrackersBoard';
import {
  ACTION_OPTIONS,
  actionTargetColumns,
  APPROVAL_EVENT_OPTIONS,
  ANY_OPTION,
  APPROVAL_STATES,
  approvalColumns,
  approvalStateLabel,
  blankAction,
  boardHasRowCreators,
  buttonColumns,
  checkboxColumns,
  copySourceColumns,
  dateTriggerColumns,
  filesColumns,
  hasDateEdges,
  hourLabel,
  isTimedTrigger,
  ruleProblem,
  authorTimeZone,
  type RuleProblem,
  operatorLabel,
  operatorTakesValue,
  peopleColumns,
  statusColumns,
  summarize,
  triggerOptionsFor,
  valueColumns,
  valueOperatorOf,
  valueOperatorsFor,
  WEEKDAYS,
  weekdayLabel,
  type ActionType,
  type ApprovalEvent,
  type TriggerColumn,
  type TriggerType,
  isBackwards,
  isEffectAction,
  isRowAction,
  MAX_ACTION_MESSAGE_LENGTH,
  AI_MODES,
  aiModeColumns,
  defaultAiMode,
  MAX_AI_INSTRUCTION_LENGTH,
  MAX_AGENT_INPUT_LENGTH,
  type AiMode,
  MAX_ACTION_TEXT_LENGTH,
} from '../../lib/automation-model';
import { trackerAvailableAgentsQueryOptions } from '@/entities/tracker-automation';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useFilterOptionLabels } from '../../lib/useFilterOptionLabels';
import { AutomationConditionsBlock } from './AutomationConditionsBlock';
import { AutomationPeoplePicker } from './AutomationPeoplePicker';
import { AutomationRowRecipientsPicker } from './AutomationRowRecipientsPicker';
import { AutomationTargetBoardSelect } from './AutomationTargetBoardSelect';
import { TriggerColumnSelect } from './TriggerColumnSelect';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';

interface AutomationRuleFieldsProps {
  trigger: AutomationTrigger;
  actions: AutomationAction[];
  conditions: AutomationConditions | null;
  /** The zone a schedule's hour is read in. */
  timeZone?: string;
  onTriggerChange: (trigger: AutomationTrigger) => void;
  onActionsChange: (actions: AutomationAction[]) => void;
  onConditionsChange: (conditions: AutomationConditions | null) => void;
  columns: CustomColumn[];
  /** What the trigger can watch: `columns` plus a record board's own fields. */
  triggerColumns: TriggerColumn[];
  /** Custom groups (free-form boards) — enables the "move to group" action. */
  customGroups: StatusOption[];
  /** Decides which triggers the board can actually reach. */
  boardType: TrackerBoardType;
  /** The saved rule's webhook signing secret, when it has one. */
  webhookSecret?: string | null;
  /** The board the rule belongs to, which a row action can target or leave. */
  trackerId: string;
  /** The workspace may use the model AI actions run on, so they are offered. */
  aiAvailable?: boolean;
  /** The person may run at least one workspace agent from a rule. */
  agentsAvailable?: boolean;
}

const LABEL = 'flex flex-col gap-1 text-xs font-medium text-muted-foreground';

let actionKeySeq = 0;
const newActionKey = () => String(++actionKeySeq);

/* eslint-disable i18next/no-literal-string -- select value ids, labelled where rendered */
const EXCEPT_PREFIX = 'except:';
const DATE_TIMING = { before: 'before', onTheDay: 'on', after: 'after' } as const;
const WHO = { people: 'people', actor: 'actor', creator: 'creator' } as const;
/* eslint-enable i18next/no-literal-string */
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

/** The server's bound on a day offset either way; past it the save is refused. */
const MAX_DAYS = 3650;

/** Whole days typed into a number field, kept to 0..MAX_DAYS; anything else reads as 0. */
const wholeDays = (raw: string) => {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? Math.min(MAX_DAYS, Math.max(0, n)) : 0;
};

/** A non-negative amount typed into a number field; anything else reads as 0. */
const amount = (raw: string) => {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.abs(n) : 0;
};

/* eslint-disable i18next/no-literal-string -- direction ids, labelled where rendered */
const DIRECTION = { forward: 'forward', back: 'back' } as const;
/* eslint-enable i18next/no-literal-string */

const PROBLEM_HINT_KEYS: Record<RuleProblem, ParseKeys<'trackers'>> = {
  triggerColumn: 'trackersBoard.automationRuleFields.pickAColumnBeforeSaving',
  triggerValue: 'trackersBoard.automationRuleFields.enterAValueToCompare',
  conditions: 'trackersBoard.automationRuleFields.eachConditionNeedsAValue',
  actionTarget: 'trackersBoard.automationRuleFields.pickWhatEachActionWrites',
  actorOnTimedTrigger: 'trackersBoard.automationRuleFields.noOneMakesTimedChanges',
  aiFillTriggersItself: 'trackersBoard.automationRuleFields.aiFillTriggersItself',
};

export function AutomationRuleFields({
  trigger,
  actions,
  conditions,
  timeZone,
  onTriggerChange,
  onActionsChange,
  onConditionsChange,
  columns,
  triggerColumns,
  customGroups,
  boardType,
  webhookSecret,
  trackerId,
  aiAvailable = false,
  agentsAvailable = false,
}: AutomationRuleFieldsProps) {
  const { t } = useTranslation('trackers');
  const labels = useFilterOptionLabels();

  const statusCols = useMemo(() => statusColumns(triggerColumns), [triggerColumns]);
  const checkboxCols = useMemo(() => checkboxColumns(triggerColumns), [triggerColumns]);
  const buttonCols = useMemo(() => buttonColumns(triggerColumns), [triggerColumns]);
  const peopleCols = useMemo(() => peopleColumns(triggerColumns), [triggerColumns]);
  const valueCols = useMemo(() => valueColumns(triggerColumns), [triggerColumns]);
  const dateCols = useMemo(() => dateTriggerColumns(triggerColumns), [triggerColumns]);
  const approvalCols = useMemo(() => approvalColumns(triggerColumns), [triggerColumns]);
  const filesCols = useMemo(() => filesColumns(triggerColumns), [triggerColumns]);
  const triggerChoices = useMemo(() => triggerOptionsFor(boardType), [boardType]);
  const recordHeading = !triggerColumns.some((c) => c.source === 'record')
    ? null
    : boardType === 'assets'
      ? t('trackersBoard.automationRuleFields.assetFields')
      : t('trackersBoard.automationRuleFields.projectFields');

  /** Build a fresh trigger of `type` with a sensible default column. */
  const blankTriggerOfType = (type: TriggerType): AutomationTrigger => {
    switch (type) {
      case 'status_changed':
        return { type, columnId: statusCols[0]?.id };
      case 'checkbox_changed':
        return { type, columnId: checkboxCols[0]?.id };
      case 'button_clicked':
        return { type, columnId: buttonCols[0]?.id };
      case 'people_changed':
        return { type, columnId: peopleCols[0]?.id };
      case 'value_changed':
        return { type, columnId: valueCols[0]?.id };
      case 'date_arrives':
        return { type, columnId: dateCols[0]?.id, dayOffset: 0 };
      case 'approval_changed':
        return { type, columnId: approvalCols[0]?.id };
      case 'files_added':
        return { type, columnId: filesCols[0]?.id };
      case 'schedule':
        return { type, hour: 9, weekdays: [1] };
      case 'approval_event':
        return { type };
      default:
        return { type: 'item_created' };
    }
  };

  // An AI action already on the rule stays choosable, so editing the rule does not drop it.
  const keepsAi = actions.some((a) => a.type === 'ai_fill');
  const keepsAgent = actions.some((a) => a.type === 'run_agent');
  // Only offer actions the board can actually perform.
  const actionChoices = useMemo(
    () =>
      ACTION_OPTIONS.filter((o) => {
        if (o.type === 'ai_fill' && !aiAvailable && !keepsAi) return false;
        if (o.type === 'run_agent' && !agentsAvailable && !keepsAgent) return false;
        if (o.type === 'move_to_group') return customGroups.length > 0;
        if (isRowAction(o.type)) return boardType === 'free_form';
        if (isEffectAction(o.type)) return true;
        return actionTargetColumns(o.type, columns).length > 0;
      }),
    [customGroups.length, columns, boardType, aiAvailable, keepsAi, agentsAvailable, keepsAgent],
  );

  // Actions carry no id of their own, and keyed by index a removed row would
  // hand its DOM and state to the row after it.
  const [keys, setKeys] = useState(() => actions.map(newActionKey));
  const actionKeys =
    keys.length === actions.length ? keys : actions.map((_, i) => keys[i] ?? `${keys.length}-${i}`);
  const setAction = (idx: number, next: AutomationAction) =>
    onActionsChange(actions.map((a, i) => (i === idx ? next : a)));
  const removeAction = (idx: number) => {
    if (actions.length <= 1) return;
    setKeys(actionKeys.filter((_, i) => i !== idx));
    onActionsChange(actions.filter((_, i) => i !== idx));
  };
  const addAction = () => {
    setKeys([...actionKeys, newActionKey()]);
    onActionsChange([...actions, blankAction(columns, trigger)]);
  };

  const previewSummary = summarize(
    trigger,
    actions,
    triggerColumns,
    customGroups,
    conditions,
    labels,
    timeZone,
  );
  const problem = ruleProblem(trigger, actions, conditions, columns);
  const statusColumn = statusCols.find((c) => c.id === trigger.columnId) ?? statusCols.at(0);
  const valueColumn = valueCols.find((c) => c.id === trigger.columnId);
  const dateColumn = dateCols.find((c) => c.id === trigger.columnId);
  const valueOperator = valueOperatorOf(trigger);
  const dateOffset = trigger.dayOffset ?? 0;
  const dateTiming =
    dateOffset > 0 ? DATE_TIMING.before : dateOffset < 0 ? DATE_TIMING.after : DATE_TIMING.onTheDay;

  return (
    <div className="flex flex-col gap-3">
      {/* WHEN */}
      <div className="rounded-xl border border-border p-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('trackersBoard.automationRuleFields.when')}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className={LABEL}>
            {t('trackersBoard.automationRuleFields.trigger')}
            <Select
              value={trigger.type}
              onValueChange={(type) => onTriggerChange(blankTriggerOfType(type as TriggerType))}
            >
              <SelectTrigger
                className="h-9"
                aria-label={t('trackersBoard.automationRuleFields.trigger')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {triggerChoices.map((o) => (
                  <SelectItem key={o.type} value={o.type}>
                    {t(o.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {trigger.type === 'status_changed' ? (
            <>
              <div className={LABEL}>
                {t('trackersBoard.automationRuleFields.column')}
                <TriggerColumnSelect
                  label={t('trackersBoard.automationRuleFields.column')}
                  value={trigger.columnId ?? ''}
                  onValueChange={(columnId) =>
                    onTriggerChange({ type: 'status_changed', columnId })
                  }
                  columns={statusCols}
                  placeholder={t('trackersBoard.automationRuleFields.pickAStatusColumn')}
                  recordHeading={recordHeading}
                />
              </div>
              <div className={LABEL}>
                {t('trackersBoard.automationRuleFields.from')}
                <Select
                  value={trigger.fromOptionId ?? ANY_OPTION}
                  onValueChange={(v) =>
                    onTriggerChange({
                      ...trigger,
                      fromOptionId: v === ANY_OPTION ? undefined : v,
                    })
                  }
                >
                  <SelectTrigger
                    className="h-9"
                    aria-label={t('trackersBoard.automationRuleFields.from')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY_OPTION}>
                      {t('trackersBoard.automationRuleFields.anyValue')}
                    </SelectItem>
                    {(statusColumn?.statusOptions ?? []).map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className={LABEL}>
                {t('trackersBoard.automationRuleFields.becomes')}
                <Select
                  value={
                    trigger.exceptOptionId
                      ? `${EXCEPT_PREFIX}${trigger.exceptOptionId}`
                      : (trigger.toOptionId ?? ANY_OPTION)
                  }
                  onValueChange={(v) =>
                    onTriggerChange({
                      ...trigger,
                      toOptionId: v === ANY_OPTION || v.startsWith(EXCEPT_PREFIX) ? undefined : v,
                      exceptOptionId: v.startsWith(EXCEPT_PREFIX)
                        ? v.slice(EXCEPT_PREFIX.length)
                        : undefined,
                    })
                  }
                >
                  <SelectTrigger
                    className="h-9"
                    aria-label={t('trackersBoard.automationRuleFields.becomes')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY_OPTION}>
                      {t('trackersBoard.automationRuleFields.anyValue')}
                    </SelectItem>
                    {(statusColumn?.statusOptions ?? []).map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.label}
                      </SelectItem>
                    ))}
                    {(statusColumn?.statusOptions ?? []).length > 0 ? (
                      <SelectGroup>
                        <SelectLabel>
                          {t('trackersBoard.automationRuleFields.anythingExcept')}
                        </SelectLabel>
                        {(statusColumn?.statusOptions ?? []).map((o) => (
                          <SelectItem key={o.id} value={`${EXCEPT_PREFIX}${o.id}`}>
                            {t('trackersBoard.automationRuleFields.anythingButValue', {
                              value: o.label,
                            })}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ) : null}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : null}

          {trigger.type === 'checkbox_changed' ? (
            <>
              <div className={LABEL}>
                {t('trackersBoard.automationRuleFields.column')}
                <TriggerColumnSelect
                  label={t('trackersBoard.automationRuleFields.column')}
                  value={trigger.columnId ?? ''}
                  onValueChange={(columnId) =>
                    onTriggerChange({ type: 'checkbox_changed', columnId, toChecked: undefined })
                  }
                  columns={checkboxCols}
                  placeholder={t('trackersBoard.automationRuleFields.pickACheckboxColumn')}
                  recordHeading={recordHeading}
                />
              </div>
              <div className={LABEL}>
                {t('trackersBoard.automationRuleFields.becomes')}
                <Select
                  /* eslint-disable i18next/no-literal-string -- checkbox bucket ids */
                  value={
                    trigger.toChecked === undefined
                      ? ANY_OPTION
                      : trigger.toChecked
                        ? 'checked'
                        : 'unchecked'
                  }
                  /* eslint-enable i18next/no-literal-string */
                  onValueChange={(v) =>
                    onTriggerChange({
                      ...trigger,
                      toChecked: v === ANY_OPTION ? undefined : v === 'checked',
                    })
                  }
                >
                  <SelectTrigger
                    className="h-9"
                    aria-label={t('trackersBoard.automationRuleFields.becomes')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY_OPTION}>
                      {t('trackersBoard.automationRuleFields.anyChange')}
                    </SelectItem>
                    {/* eslint-disable-next-line i18next/no-literal-string -- checkbox bucket id */}
                    <SelectItem value="checked">
                      {t('trackersBoard.automationRuleFields.checked')}
                    </SelectItem>
                    {/* eslint-disable-next-line i18next/no-literal-string -- checkbox bucket id */}
                    <SelectItem value="unchecked">
                      {t('trackersBoard.automationRuleFields.unchecked')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : null}

          {trigger.type === 'button_clicked' ? (
            <div className={LABEL}>
              {t('trackersBoard.automationRuleFields.button')}
              <TriggerColumnSelect
                label={t('trackersBoard.automationRuleFields.button')}
                value={trigger.columnId ?? ''}
                onValueChange={(columnId) => onTriggerChange({ type: 'button_clicked', columnId })}
                columns={buttonCols}
                placeholder={t('trackersBoard.automationRuleFields.pickAButtonColumn')}
                recordHeading={recordHeading}
              />
            </div>
          ) : null}

          {trigger.type === 'people_changed' ? (
            <div className={LABEL}>
              {t('trackersBoard.automationRuleFields.column')}
              <TriggerColumnSelect
                label={t('trackersBoard.automationRuleFields.column')}
                value={trigger.columnId ?? ''}
                onValueChange={(columnId) => onTriggerChange({ type: 'people_changed', columnId })}
                columns={peopleCols}
                placeholder={t('trackersBoard.automationRuleFields.pickAPeopleColumn')}
                recordHeading={recordHeading}
              />
            </div>
          ) : null}

          {trigger.type === 'value_changed' ? (
            <>
              <div className={LABEL}>
                {t('trackersBoard.automationRuleFields.column')}
                <TriggerColumnSelect
                  label={t('trackersBoard.automationRuleFields.column')}
                  value={trigger.columnId ?? ''}
                  onValueChange={(columnId) =>
                    onTriggerChange({ type: 'value_changed', columnId, toValue: undefined })
                  }
                  columns={valueCols}
                  placeholder={t('trackersBoard.automationRuleFields.pickAField')}
                  recordHeading={recordHeading}
                />
              </div>
              <>
                <div className={LABEL}>
                  {t('trackersBoard.automationRuleFields.becomes')}
                  <Select
                    value={valueOperator ?? ANY_OPTION}
                    onValueChange={(v) =>
                      onTriggerChange({
                        type: 'value_changed',
                        columnId: trigger.columnId,
                        operator: v === ANY_OPTION ? undefined : (v as AutomationValueOperator),
                        toValue: v === ANY_OPTION ? undefined : trigger.toValue,
                      })
                    }
                  >
                    <SelectTrigger
                      className="h-9"
                      aria-label={t('trackersBoard.automationRuleFields.becomes')}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ANY_OPTION}>
                        {t('trackersBoard.automationRuleFields.anyChange')}
                      </SelectItem>
                      {valueOperatorsFor(valueColumn).map((operator) => (
                        <SelectItem key={operator} value={operator}>
                          {operatorLabel(operator)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {valueOperator && operatorTakesValue(valueOperator) ? (
                  <div className={LABEL}>
                    {valueOperator === 'between'
                      ? t('trackersBoard.automationRuleFields.fromAndTo')
                      : t('trackersBoard.automationRuleFields.value')}
                    <div className="flex gap-2">
                      <Input
                        className="h-9"
                        value={trigger.toValue ?? ''}
                        aria-label={t('trackersBoard.automationRuleFields.value')}
                        onChange={(e) => onTriggerChange({ ...trigger, toValue: e.target.value })}
                      />
                      {valueOperator === 'between' ? (
                        <Input
                          className="h-9"
                          value={trigger.toValueMax ?? ''}
                          aria-label={t('trackersBoard.automationRuleFields.upTo')}
                          onChange={(e) =>
                            onTriggerChange({ ...trigger, toValueMax: e.target.value })
                          }
                        />
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </>
            </>
          ) : null}

          {trigger.type === 'date_arrives' ? (
            <>
              <div className={LABEL}>
                {t('trackersBoard.automationRuleFields.dateColumn')}
                <TriggerColumnSelect
                  label={t('trackersBoard.automationRuleFields.dateColumn')}
                  value={trigger.columnId ?? ''}
                  onValueChange={(columnId) =>
                    onTriggerChange({ ...trigger, type: 'date_arrives', columnId })
                  }
                  columns={dateCols}
                  placeholder={t('trackersBoard.automationRuleFields.pickADateColumn')}
                  recordHeading={recordHeading}
                />
              </div>
              <>
                <div className={LABEL}>
                  {t('trackersBoard.automationRuleFields.when')}
                  <div className="flex gap-2">
                    {dateTiming !== DATE_TIMING.onTheDay ? (
                      <Input
                        type="number"
                        min={1}
                        className="h-9 w-20 shrink-0"
                        value={Math.abs(dateOffset)}
                        aria-label={t('trackersBoard.automationRuleFields.days')}
                        onChange={(e) => {
                          const days = Math.max(1, wholeDays(e.target.value));
                          onTriggerChange({
                            ...trigger,
                            dayOffset: dateTiming === DATE_TIMING.after ? -days : days,
                          });
                        }}
                      />
                    ) : null}
                    <Select
                      value={dateTiming}
                      onValueChange={(v) =>
                        onTriggerChange({
                          ...trigger,
                          dayOffset:
                            v === DATE_TIMING.onTheDay
                              ? 0
                              : (v === DATE_TIMING.after ? -1 : 1) *
                                Math.max(1, Math.abs(dateOffset)),
                        })
                      }
                    >
                      <SelectTrigger
                        className="h-9 flex-1"
                        aria-label={t('trackersBoard.automationRuleFields.when')}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={DATE_TIMING.before}>
                          {t('trackersBoard.automationRuleFields.daysBefore')}
                        </SelectItem>
                        <SelectItem value={DATE_TIMING.onTheDay}>
                          {t('trackersBoard.automationRuleFields.onTheDay')}
                        </SelectItem>
                        <SelectItem value={DATE_TIMING.after}>
                          {t('trackersBoard.automationRuleFields.daysAfter')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {hasDateEdges(dateColumn) ? (
                  <div className={LABEL}>
                    {t('trackersBoard.automationRuleFields.watches')}
                    <Select
                      // eslint-disable-next-line i18next/no-literal-string -- edge id
                      value={trigger.dateEdge ?? 'start'}
                      onValueChange={(v) =>
                        onTriggerChange({ ...trigger, dateEdge: v as 'start' | 'end' })
                      }
                    >
                      <SelectTrigger
                        className="h-9"
                        aria-label={t('trackersBoard.automationRuleFields.watches')}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {/* eslint-disable-next-line i18next/no-literal-string -- edge id */}
                        <SelectItem value="start">
                          {dateColumn?.type === 'timeline'
                            ? t('trackersBoard.automationRuleFields.eachPhaseStart')
                            : t('trackersBoard.automationRuleFields.theStart')}
                        </SelectItem>
                        {/* eslint-disable-next-line i18next/no-literal-string -- edge id */}
                        <SelectItem value="end">
                          {dateColumn?.type === 'timeline'
                            ? t('trackersBoard.automationRuleFields.eachPhaseEnd')
                            : t('trackersBoard.automationRuleFields.theEnd')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </>
            </>
          ) : null}

          {trigger.type === 'approval_changed' ? (
            <>
              <div className={LABEL}>
                {t('trackersBoard.automationRuleFields.column')}
                <TriggerColumnSelect
                  label={t('trackersBoard.automationRuleFields.column')}
                  value={trigger.columnId ?? ''}
                  onValueChange={(columnId) =>
                    onTriggerChange({ type: 'approval_changed', columnId })
                  }
                  columns={approvalCols}
                  placeholder={t('trackersBoard.automationRuleFields.pickAnApprovalColumn')}
                  recordHeading={recordHeading}
                />
              </div>
              <div className={LABEL}>
                {t('trackersBoard.automationRuleFields.becomes')}
                <Select
                  value={trigger.toState ?? ANY_OPTION}
                  onValueChange={(v) =>
                    onTriggerChange({
                      ...trigger,
                      toState: v === ANY_OPTION ? undefined : (v as AutomationTrigger['toState']),
                    })
                  }
                >
                  <SelectTrigger
                    className="h-9"
                    aria-label={t('trackersBoard.automationRuleFields.becomes')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY_OPTION}>
                      {t('trackersBoard.automationRuleFields.anyChange')}
                    </SelectItem>
                    {APPROVAL_STATES.map((state) => (
                      <SelectItem key={state} value={state}>
                        {approvalStateLabel(state)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : null}

          {trigger.type === 'files_added' ? (
            <div className={LABEL}>
              {t('trackersBoard.automationRuleFields.column')}
              <TriggerColumnSelect
                label={t('trackersBoard.automationRuleFields.column')}
                value={trigger.columnId ?? ''}
                onValueChange={(columnId) => onTriggerChange({ type: 'files_added', columnId })}
                columns={filesCols}
                placeholder={t('trackersBoard.automationRuleFields.pickAFilesColumn')}
                recordHeading={recordHeading}
              />
            </div>
          ) : null}

          {trigger.type === 'schedule' ? (
            <div className={LABEL}>
              {t('trackersBoard.automationRuleFields.at')}
              <Select
                value={String(trigger.hour ?? 9)}
                onValueChange={(v) => onTriggerChange({ ...trigger, hour: Number(v) })}
              >
                <SelectTrigger
                  className="h-9"
                  aria-label={t('trackersBoard.automationRuleFields.at')}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map((hour) => (
                    <SelectItem key={hour} value={String(hour)}>
                      {hourLabel(hour)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {timeZone && timeZone !== authorTimeZone() ? (
                <span className="font-normal">
                  {t('trackersBoard.automationRuleFields.inZone', { zone: timeZone })}
                </span>
              ) : null}
            </div>
          ) : null}

          {trigger.type === 'approval_event' ? (
            <div className={LABEL}>
              {t('trackersBoard.automationRuleFields.approvalDecision')}
              <Select
                value={trigger.approvalEvent ?? ANY_OPTION}
                onValueChange={(v) =>
                  onTriggerChange({
                    type: 'approval_event',
                    approvalEvent: v === ANY_OPTION ? undefined : (v as ApprovalEvent),
                  })
                }
              >
                <SelectTrigger
                  className="h-9"
                  aria-label={t('trackersBoard.automationRuleFields.approvalDecision')}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_OPTION}>
                    {t('trackersBoard.automationRuleFields.anyDecision')}
                  </SelectItem>
                  {APPROVAL_EVENT_OPTIONS.map((o) => (
                    <SelectItem key={o.event} value={o.event}>
                      {t(o.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>

        {trigger.type === 'schedule' ? (
          <div className={`${LABEL} mt-3`}>
            {t('trackersBoard.automationRuleFields.onTheseDays')}
            <ToggleGroup
              type="multiple"
              variant="outline"
              size="sm"
              aria-label={t('trackersBoard.automationRuleFields.onTheseDays')}
              value={(trigger.weekdays ?? []).map(String)}
              onValueChange={(days) =>
                onTriggerChange({ ...trigger, weekdays: days.map(Number).sort((a, b) => a - b) })
              }
            >
              {WEEKDAYS.map((day) => (
                <ToggleGroupItem key={day} value={String(day)} aria-label={weekdayLabel(day)}>
                  {weekdayLabel(day).slice(0, 3)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <span className="font-normal">
              {t('trackersBoard.automationRuleFields.noDayMeansEveryDay')}
            </span>
          </div>
        ) : null}
      </div>

      <AutomationConditionsBlock
        conditions={conditions}
        onChange={onConditionsChange}
        columns={columns}
        boardType={boardType}
      />

      <div className="flex justify-center text-muted-foreground">
        <ArrowDown className="size-4" aria-hidden />
      </div>

      {/* THEN */}
      <div className="rounded-xl border border-border p-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('trackersBoard.automationRuleFields.then')}
        </div>
        <div className="flex flex-col gap-2">
          {actions.map((action, idx) => (
            <ActionRow
              key={actionKeys[idx]}
              action={action}
              choices={actionChoices}
              columns={columns}
              customGroups={customGroups}
              boardType={boardType}
              timedTrigger={isTimedTrigger(trigger)}
              webhookSecret={webhookSecret}
              trackerId={trackerId}
              onChange={(next) => setAction(idx, next)}
              onRemove={actions.length > 1 ? () => removeAction(idx) : undefined}
            />
          ))}
          <Button
            type="button"
            variant="ghost"
            size="mini"
            onClick={addAction}
            className="self-start text-muted-foreground hover:text-foreground"
          >
            <Plus className="size-3.5" aria-hidden />
            {t('trackersBoard.automationRuleFields.addAction')}
          </Button>
        </div>
      </div>

      <p className="rounded-lg bg-muted px-3 py-2 text-sm text-foreground">{previewSummary}</p>

      {/* Says why Create is greyed out, reading the same check the button does.
          A record board starts with no columns of its own, so neither half of
          the rule has anything to point at yet. */}
      {problem ? (
        <p className="text-sm text-muted-foreground">{t(PROBLEM_HINT_KEYS[problem])}</p>
      ) : null}
    </div>
  );
}

function ActionRow({
  action,
  choices,
  columns,
  customGroups,
  boardType,
  timedTrigger,
  webhookSecret,
  trackerId,
  onChange,
  onRemove,
}: {
  action: AutomationAction;
  choices: ReadonlyArray<{ type: ActionType; labelKey: ParseKeys<'trackers'> }>;
  columns: CustomColumn[];
  customGroups: StatusOption[];
  boardType: TrackerBoardType;
  /** A schedule or a date fires the rule, so no person makes the change. */
  timedTrigger: boolean;
  /** The secret the saved rule signs its webhook requests with, shown to whoever manages it. */
  webhookSecret?: string | null;
  trackerId: string;
  onChange: (next: AutomationAction) => void;
  onRemove?: () => void;
}) {
  const { t } = useTranslation('trackers');

  const targets = actionTargetColumns(action.type, columns);
  const statusColumn = targets.find((c) => c.id === action.columnId) ?? targets.at(0);

  const pickType = (type: ActionType): AutomationAction => {
    const first = actionTargetColumns(type, columns).at(0);
    switch (type) {
      case 'set_status':
        return { type, columnId: first?.id, optionId: first?.statusOptions?.[0]?.id };
      case 'move_to_group':
        return { type, groupId: customGroups[0]?.id };
      case 'set_checkbox':
        return { type, columnId: first?.id, value: true };
      case 'assign_people':
        return { type, columnId: first?.id, userIds: [] };
      case 'set_number':
        return { type, columnId: first?.id, numberValue: 0 };
      case 'set_text':
        return { type, columnId: first?.id, textValue: '' };
      case 'shift_date':
        return { type, columnId: first?.id, dayOffset: 7 };
      case 'increment_number':
        return { type, columnId: first?.id, numberValue: 1 };
      case 'copy_value':
        return {
          type,
          columnId: first?.id,
          sourceColumnId: copySourceColumns(columns, first?.id).at(0)?.id,
        };
      case 'notify':
        return { type, userIds: [] };
      case 'comment':
        return { type, message: '' };
      case 'webhook':
        return { type, url: '' };
      case 'slack':
        return { type, url: '', message: '' };
      case 'create_row':
        return { type, textValue: '' };
      case 'duplicate_row':
      case 'archive_row':
      case 'move_row':
        return { type };
      case 'ai_fill':
        return { type, columnId: first?.id, aiMode: defaultAiMode(first), instruction: '' };
      case 'run_agent':
        return { type, agentInput: '' };
      default:
        return { type, columnId: first?.id };
    }
  };

  const columnSelect = (
    placeholder: string,
    width = 'flex-1',
    onPick: (columnId: string) => void = (columnId) => onChange({ ...action, columnId }),
  ) => (
    <Select value={action.columnId ?? ''} onValueChange={onPick}>
      <SelectTrigger className={`h-9 ${width}`} aria-label={placeholder}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {targets.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  const who = action.userRef ?? WHO.people;
  const messageField = (label: string) => (
    <Textarea
      className="basis-full"
      rows={2}
      maxLength={MAX_ACTION_MESSAGE_LENGTH}
      aria-label={label}
      placeholder={label}
      value={action.message ?? ''}
      onChange={(e) => onChange({ ...action, message: e.target.value })}
    />
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={action.type} onValueChange={(next) => onChange(pickType(next as ActionType))}>
        <SelectTrigger
          className="h-9 w-44 shrink-0"
          aria-label={t('trackersBoard.automationRuleFields.action')}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {choices.map((o) => (
            <SelectItem key={o.type} value={o.type}>
              {t(o.labelKey)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {action.type === 'set_status' ? (
        <>
          <Select
            value={action.columnId ?? ''}
            onValueChange={(columnId) =>
              onChange({ type: 'set_status', columnId, optionId: undefined })
            }
          >
            <SelectTrigger
              className="h-9 flex-1"
              aria-label={t('trackersBoard.automationRuleFields.column')}
            >
              <SelectValue placeholder={t('trackersBoard.automationRuleFields.column')} />
            </SelectTrigger>
            <SelectContent>
              {targets.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={action.optionId ?? ''}
            onValueChange={(optionId) => onChange({ ...action, optionId })}
          >
            <SelectTrigger
              className="h-9 flex-1"
              aria-label={t('trackersBoard.automationRuleFields.value')}
            >
              <SelectValue placeholder={t('trackersBoard.automationRuleFields.value')} />
            </SelectTrigger>
            <SelectContent>
              {(statusColumn?.statusOptions ?? []).map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      ) : null}

      {action.type === 'move_to_group' ? (
        <Select
          value={action.groupId ?? ''}
          onValueChange={(groupId) => onChange({ type: 'move_to_group', groupId })}
        >
          <SelectTrigger
            className="h-9 flex-1"
            aria-label={t('trackersBoard.automationRuleFields.group')}
          >
            <SelectValue placeholder={t('trackersBoard.automationRuleFields.group')} />
          </SelectTrigger>
          <SelectContent>
            {customGroups.map((g) => (
              <SelectItem key={g.id} value={g.id}>
                {g.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      {action.type === 'set_checkbox' ? (
        <>
          <Select
            value={action.columnId ?? ''}
            onValueChange={(columnId) => onChange({ ...action, columnId })}
          >
            <SelectTrigger
              className="h-9 flex-1"
              aria-label={t('trackersBoard.automationRuleFields.column')}
            >
              <SelectValue placeholder={t('trackersBoard.automationRuleFields.column')} />
            </SelectTrigger>
            <SelectContent>
              {targets.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            // eslint-disable-next-line i18next/no-literal-string -- checkbox bucket ids
            value={action.value ? 'checked' : 'unchecked'}
            onValueChange={(v) => onChange({ ...action, value: v === 'checked' })}
          >
            <SelectTrigger
              className="h-9 w-32 shrink-0"
              aria-label={t('trackersBoard.automationRuleFields.value')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* eslint-disable-next-line i18next/no-literal-string -- checkbox bucket id */}
              <SelectItem value="checked">
                {t('trackersBoard.automationRuleFields.checked')}
              </SelectItem>
              {/* eslint-disable-next-line i18next/no-literal-string -- checkbox bucket id */}
              <SelectItem value="unchecked">
                {t('trackersBoard.automationRuleFields.unchecked')}
              </SelectItem>
            </SelectContent>
          </Select>
        </>
      ) : null}

      {action.type === 'set_date_today' ? (
        <>
          {columnSelect(t('trackersBoard.automationRuleFields.dateColumn'))}
          <label className="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
            {t('trackersBoard.automationRuleFields.plus')}
            <Input
              type="number"
              min={0}
              className="h-9 w-20"
              value={action.dayOffset ?? 0}
              onChange={(e) =>
                onChange({ ...action, dayOffset: Math.max(0, wholeDays(e.target.value)) })
              }
            />
            {t('trackersBoard.automationRuleFields.days')}
          </label>
        </>
      ) : null}

      {action.type === 'shift_date' ? (
        <>
          {columnSelect(t('trackersBoard.automationRuleFields.dateColumn'))}
          <DirectionPicker
            back={isBackwards(action.dayOffset)}
            backLabel={t('trackersBoard.automationRuleFields.earlier')}
            forwardLabel={t('trackersBoard.automationRuleFields.later')}
            onChange={(back) => {
              const days = Math.max(1, Math.abs(action.dayOffset ?? 0));
              onChange({ ...action, dayOffset: back ? -days : days });
            }}
          />
          <label className="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground">
            {t('trackersBoard.automationRuleFields.by')}
            <Input
              type="number"
              min={0}
              max={MAX_DAYS}
              className="h-9 w-20"
              value={Math.abs(action.dayOffset ?? 0)}
              onChange={(e) => {
                const days = wholeDays(e.target.value);
                onChange({ ...action, dayOffset: isBackwards(action.dayOffset) ? -days : days });
              }}
            />
            {t('trackersBoard.automationRuleFields.days')}
          </label>
        </>
      ) : null}

      {action.type === 'increment_number' ? (
        <>
          {columnSelect(t('trackersBoard.automationRuleFields.numberColumn'))}
          <DirectionPicker
            back={isBackwards(action.numberValue)}
            backLabel={t('trackersBoard.automationRuleFields.subtract')}
            forwardLabel={t('trackersBoard.automationRuleFields.add')}
            onChange={(back) => {
              const by = Math.abs(action.numberValue ?? 0) || 1;
              onChange({ ...action, numberValue: back ? -by : by });
            }}
          />
          <Input
            type="number"
            min={0}
            className="h-9 w-24 shrink-0"
            aria-label={t('trackersBoard.automationRuleFields.value')}
            value={Math.abs(action.numberValue ?? 0)}
            onChange={(e) => {
              const by = amount(e.target.value);
              onChange({ ...action, numberValue: isBackwards(action.numberValue) ? -by : by });
            }}
          />
        </>
      ) : null}

      {action.type === 'copy_value' ? (
        <>
          {columnSelect(t('trackersBoard.automationRuleFields.into'), 'flex-1', (columnId) =>
            onChange({
              ...action,
              columnId,
              sourceColumnId: copySourceColumns(columns, columnId).at(0)?.id,
            }),
          )}
          <span className="shrink-0 text-sm text-muted-foreground">
            {t('trackersBoard.automationRuleFields.from')}
          </span>
          <Select
            value={action.sourceColumnId ?? ''}
            onValueChange={(sourceColumnId) => onChange({ ...action, sourceColumnId })}
          >
            <SelectTrigger
              className="h-9 flex-1"
              aria-label={t('trackersBoard.automationRuleFields.from')}
            >
              <SelectValue placeholder={t('trackersBoard.automationRuleFields.sameTypeColumn')} />
            </SelectTrigger>
            <SelectContent>
              {copySourceColumns(columns, action.columnId).map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      ) : null}

      {action.type === 'assign_people' ? (
        <>
          <Select
            value={action.columnId ?? ''}
            onValueChange={(columnId) => onChange({ ...action, columnId })}
          >
            <SelectTrigger
              className="h-9 w-40 shrink-0"
              aria-label={t('trackersBoard.automationRuleFields.column')}
            >
              <SelectValue placeholder={t('trackersBoard.automationRuleFields.column')} />
            </SelectTrigger>
            <SelectContent>
              {targets.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={who}
            onValueChange={(v) =>
              onChange(
                v === WHO.people
                  ? { type: 'assign_people', columnId: action.columnId, userIds: [] }
                  : {
                      type: 'assign_people',
                      columnId: action.columnId,
                      userRef: v as NonNullable<AutomationAction['userRef']>,
                    },
              )
            }
          >
            <SelectTrigger
              className="h-9 w-44 shrink-0"
              aria-label={t('trackersBoard.automationRuleFields.assignTo')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={WHO.people}>
                {t('trackersBoard.automationRuleFields.specificPeople')}
              </SelectItem>
              {timedTrigger ? null : (
                <SelectItem value={WHO.actor}>
                  {t('trackersBoard.automationRuleFields.whoeverMadeTheChange')}
                </SelectItem>
              )}
              {boardHasRowCreators(boardType) ? (
                <SelectItem value={WHO.creator}>
                  {t('trackersBoard.automationRuleFields.whoeverCreatedTheRow')}
                </SelectItem>
              ) : null}
            </SelectContent>
          </Select>
          {who === WHO.people ? (
            <AutomationPeoplePicker
              value={action.userIds ?? []}
              onChange={(userIds) => onChange({ ...action, userIds })}
            />
          ) : null}
        </>
      ) : null}

      {action.type === 'set_number' ? (
        <>
          <Select
            value={action.columnId ?? ''}
            onValueChange={(columnId) => onChange({ ...action, columnId })}
          >
            <SelectTrigger
              className="h-9 flex-1"
              aria-label={t('trackersBoard.automationRuleFields.numberColumn')}
            >
              <SelectValue placeholder={t('trackersBoard.automationRuleFields.numberColumn')} />
            </SelectTrigger>
            <SelectContent>
              {targets.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            className="h-9 w-28 shrink-0"
            aria-label={t('trackersBoard.automationRuleFields.value')}
            value={action.numberValue ?? 0}
            onChange={(e) => onChange({ ...action, numberValue: Number(e.target.value) })}
          />
        </>
      ) : null}

      {action.type === 'set_text' ? (
        <>
          <Select
            value={action.columnId ?? ''}
            onValueChange={(columnId) => onChange({ ...action, columnId })}
          >
            <SelectTrigger
              className="h-9 w-40 shrink-0"
              aria-label={t('trackersBoard.automationRuleFields.column')}
            >
              <SelectValue placeholder={t('trackersBoard.automationRuleFields.column')} />
            </SelectTrigger>
            <SelectContent>
              {targets.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            className="h-9 flex-1"
            value={action.textValue ?? ''}
            maxLength={MAX_ACTION_TEXT_LENGTH}
            aria-label={t('trackersBoard.automationRuleFields.textToSet')}
            placeholder={t('trackersBoard.automationRuleFields.textToSet')}
            onChange={(e) => onChange({ ...action, textValue: e.target.value })}
          />
        </>
      ) : null}

      {action.type === 'ai_fill' ? (
        <AiFillFields action={action} columns={columns} onChange={onChange} />
      ) : null}

      {action.type === 'run_agent' ? <AgentFields action={action} onChange={onChange} /> : null}

      {action.type === 'clear_value' ? (
        <Select
          value={action.columnId ?? ''}
          onValueChange={(columnId) => onChange({ type: 'clear_value', columnId })}
        >
          <SelectTrigger
            className="h-9 flex-1"
            aria-label={t('trackersBoard.automationRuleFields.fieldToClear')}
          >
            <SelectValue placeholder={t('trackersBoard.automationRuleFields.fieldToClear')} />
          </SelectTrigger>
          <SelectContent>
            {targets.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      {action.type === 'notify' ? (
        <>
          <AutomationPeoplePicker
            value={action.userIds ?? []}
            onChange={(userIds) => onChange({ ...action, userIds })}
            emptyLabel={t('trackersBoard.automationRuleFields.specificPeopleToEmail')}
          />
          <AutomationRowRecipientsPicker
            refs={action.recipientRefs ?? []}
            columnIds={action.recipientColumnIds ?? []}
            peopleColumns={peopleColumns(columns)}
            offerActor={!timedTrigger}
            offerCreator={boardHasRowCreators(boardType)}
            onChange={(recipients) => onChange({ ...action, ...recipients })}
          />
        </>
      ) : null}

      {action.type === 'create_row' ||
      action.type === 'duplicate_row' ||
      action.type === 'move_row' ? (
        <AutomationTargetBoardSelect
          trackerId={trackerId}
          value={action.targetTrackerId}
          allowThisBoard={action.type !== 'move_row'}
          onChange={(targetTrackerId) => onChange({ ...action, targetTrackerId })}
        />
      ) : null}

      {action.type === 'create_row' ? (
        <Input
          className="h-9 flex-1"
          aria-label={t('trackersBoard.automationRuleFields.newRowTitle')}
          placeholder={t('trackersBoard.automationRuleFields.newRowTitle')}
          value={action.textValue ?? ''}
          maxLength={MAX_ACTION_TEXT_LENGTH}
          onChange={(e) => onChange({ ...action, textValue: e.target.value })}
        />
      ) : null}

      {action.type === 'comment' ? (
        <AutomationPeoplePicker
          value={action.userIds ?? []}
          onChange={(userIds) => onChange({ ...action, userIds })}
          emptyLabel={t('trackersBoard.automationRuleFields.mentionPeople')}
        />
      ) : null}

      {onRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-mini"
          aria-label={t('trackersBoard.automationRuleFields.removeAction')}
          onClick={onRemove}
          className="shrink-0 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="size-3.5" aria-hidden />
        </Button>
      ) : null}

      {action.type === 'notify' ? (
        <>
          {messageField(t('trackersBoard.automationRuleFields.emailNote'))}
          <p className="basis-full text-xs text-muted-foreground">
            {t('trackersBoard.automationRuleFields.emailsGoTo')}
          </p>
        </>
      ) : null}

      {action.type === 'webhook' || action.type === 'slack' ? (
        <Input
          type="url"
          className="h-9 basis-full"
          aria-label={
            action.type === 'slack'
              ? t('trackersBoard.automationRuleFields.slackWebhookUrl')
              : t('trackersBoard.automationRuleFields.webhookUrl')
          }
          placeholder={
            action.urlPreview
              ? t('trackersBoard.automationRuleFields.savedUrl', { preview: action.urlPreview })
              : action.type === 'slack'
                ? t('trackersBoard.automationRuleFields.slackWebhookUrlPlaceholder')
                : t('trackersBoard.automationRuleFields.webhookUrlPlaceholder')
          }
          value={action.url ?? ''}
          // A typed URL wins on the server; clearing the field falls back to the saved key.
          onChange={(e) => onChange({ ...action, url: e.target.value.trim() || undefined })}
        />
      ) : null}

      {action.type === 'webhook' ? (
        <p className="basis-full text-xs text-muted-foreground">
          {webhookSecret
            ? t('trackersBoard.automationRuleFields.webhookSignedWith', { secret: webhookSecret })
            : t('trackersBoard.automationRuleFields.webhookSignedOnceSaved')}
        </p>
      ) : null}

      {action.type === 'slack' ? (
        <>
          {messageField(t('trackersBoard.automationRuleFields.slackLine'))}
          <p className="basis-full text-xs text-muted-foreground">
            {t('trackersBoard.automationRuleFields.slackCarriesValues')}
          </p>
        </>
      ) : null}

      {action.type === 'move_row' || action.type === 'duplicate_row' ? (
        <p className="basis-full text-xs text-muted-foreground">
          {action.targetTrackerId
            ? t('trackersBoard.automationRuleFields.rowCellsCarryOver')
            : t('trackersBoard.automationRuleFields.rowCellsAllCarryOver')}
        </p>
      ) : null}

      {action.type === 'archive_row' ? (
        <p className="basis-full text-xs text-muted-foreground">
          {t('trackersBoard.automationRuleFields.archivedRowsLeave')}
        </p>
      ) : null}

      {action.type === 'comment' ? (
        <>
          {messageField(t('trackersBoard.automationRuleFields.commentText'))}
          <p className="basis-full text-xs text-muted-foreground">
            {t('trackersBoard.automationRuleFields.commentSignedByRule')}
          </p>
        </>
      ) : null}
    </div>
  );
}

/**
 * Which way a shift or an add goes, kept apart from its amount: a minus typed
 * into a number field reads as nothing until a digit follows, and the field
 * would put a 0 back in its place.
 */
function AiFillFields({
  action,
  columns,
  onChange,
}: {
  action: AutomationAction;
  columns: CustomColumn[];
  onChange: (next: AutomationAction) => void;
}) {
  const { t } = useTranslation('trackers');
  const targets = aiModeColumns(action.aiMode, columns);
  const pickMode = (aiMode: AiMode) => {
    const fits = aiModeColumns(aiMode, columns);
    const columnId = fits.some((c) => c.id === action.columnId) ? action.columnId : fits.at(0)?.id;
    onChange({ ...action, aiMode, columnId });
  };
  return (
    <>
      <Select value={action.aiMode ?? ''} onValueChange={(next) => pickMode(next as AiMode)}>
        <SelectTrigger
          className="h-9 w-36 shrink-0"
          aria-label={t('trackersBoard.automationAi.fill.modeLabel')}
        >
          <SelectValue placeholder={t('trackersBoard.automationAi.fill.modeLabel')} />
        </SelectTrigger>
        <SelectContent>
          {AI_MODES.map((mode) => (
            <SelectItem
              key={mode}
              value={mode}
              disabled={aiModeColumns(mode, columns).length === 0}
            >
              {t(`trackersBoard.automationAi.fill.modes.${mode}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={action.columnId ?? ''}
        onValueChange={(columnId) => onChange({ ...action, columnId })}
      >
        <SelectTrigger
          className="h-9 flex-1"
          aria-label={t('trackersBoard.automationAi.fill.columnLabel')}
        >
          <SelectValue placeholder={t('trackersBoard.automationAi.fill.columnLabel')} />
        </SelectTrigger>
        <SelectContent>
          {targets.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              {c.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Textarea
        className="basis-full"
        rows={2}
        maxLength={MAX_AI_INSTRUCTION_LENGTH}
        aria-label={t('trackersBoard.automationAi.fill.instructionLabel')}
        placeholder={t('trackersBoard.automationAi.fill.instructionPlaceholder')}
        value={action.instruction ?? ''}
        onChange={(e) => onChange({ ...action, instruction: e.target.value })}
      />
      <p className="basis-full text-xs text-muted-foreground">
        {t('trackersBoard.automationAi.fill.hint')} {t('trackersBoard.automationAi.untrustedNote')}
      </p>
    </>
  );
}

function AgentFields({
  action,
  onChange,
}: {
  action: AutomationAction;
  onChange: (next: AutomationAction) => void;
}) {
  const { t } = useTranslation('trackers');
  const workspaceId = useCurrentWorkspaceId();
  const { data } = useQuery(trackerAvailableAgentsQueryOptions(workspaceId));
  const agents = data?.data ?? [];
  // A rule someone else wrote can name an agent this editor may not run, and
  // so is not listed; it stays choosable, or the select reads empty and the
  // edit looks like it drops the agent.
  const [savedAgentId] = useState(action.agentId);
  const keepsSaved = !!savedAgentId && !agents.some((agent) => agent.id === savedAgentId);
  return (
    <>
      <Select
        value={action.agentId ?? ''}
        onValueChange={(agentId) => onChange({ ...action, agentId })}
      >
        <SelectTrigger
          className="h-9 flex-1"
          aria-label={t('trackersBoard.automationAi.agent.agentLabel')}
        >
          <SelectValue placeholder={t('trackersBoard.automationAi.agent.agentLabel')} />
        </SelectTrigger>
        <SelectContent>
          {keepsSaved && (
            <SelectItem value={savedAgentId}>
              {t('trackersBoard.automationAi.agent.currentAgent')}
            </SelectItem>
          )}
          {agents.map((agent) => (
            <SelectItem key={agent.id} value={agent.id}>
              {agent.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Textarea
        className="basis-full"
        rows={2}
        maxLength={MAX_AGENT_INPUT_LENGTH}
        aria-label={t('trackersBoard.automationAi.agent.inputLabel')}
        placeholder={t('trackersBoard.automationAi.agent.inputPlaceholder')}
        value={action.agentInput ?? ''}
        onChange={(e) => onChange({ ...action, agentInput: e.target.value })}
      />
      <p className="basis-full text-xs text-muted-foreground">
        {t('trackersBoard.automationAi.agent.hint')} {t('trackersBoard.automationAi.untrustedNote')}
      </p>
    </>
  );
}

function DirectionPicker({
  back,
  backLabel,
  forwardLabel,
  onChange,
}: {
  back: boolean;
  backLabel: string;
  forwardLabel: string;
  onChange: (back: boolean) => void;
}) {
  const { t } = useTranslation('trackers');
  return (
    <Select
      value={back ? DIRECTION.back : DIRECTION.forward}
      onValueChange={(v) => onChange(v === DIRECTION.back)}
    >
      <SelectTrigger
        className="h-9 w-32 shrink-0"
        aria-label={t('trackersBoard.automationRuleFields.direction')}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={DIRECTION.forward}>{forwardLabel}</SelectItem>
        <SelectItem value={DIRECTION.back}>{backLabel}</SelectItem>
      </SelectContent>
    </Select>
  );
}
